import { Alert, Button, Select } from '@piggytechng/component-library';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { OverideButtonStyle, OverideSelectStyle } from '../../../../helpers/utils';
import useManagedAccountHolds from '../../../../hooks/useManagedAccountHolds';
import ModalExecuteAction from '../../../../components/Modals/ModalExecuteAction';
import { localStorageEnum } from '../../../../api/enums';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { setModalActions } from '../../../../store/slices/utils';
import { httpClient } from '../../../../api/http';
import { ManagedAccount } from '../../../../types';

export default function HoldManager({ UserId }: any) {
  const [holdTab, setHoldTab] = useState<'hold' | 'unhold'>('hold');
  const [reason, setReason] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedRef, setSelectedRef] = useState('');
  const { isRunningHoldAction, placeHold, clearHold, riskView } = useManagedAccountHolds();
  const modalActions = useSelector((state: RootState) => state.modalActions);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const style = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };

  const userName = `${localStorage.getItem(localStorageEnum['local.storage.firstName'])} ${localStorage.getItem(localStorageEnum['local.storage.lastName'])}`;

  // The hold endpoints key off the managed-account ref, not the customer's user ID.
  const accountsQuery = useQuery<{ items: ManagedAccount[] }>({
    queryKey: ['customer-managed-accounts', UserId],
    enabled: !!UserId,
    retry: false,
    queryFn: () =>
      httpClient
        .get(`admin/user/${encodeURIComponent(UserId)}/managed-accounts`)
        .then((response) => response.data.data),
  });
  const accounts = useMemo(() => accountsQuery.data?.items || [], [accountsQuery.data]);

  useEffect(() => {
    if (accounts.length && !accounts.some((account) => account.ref === selectedRef)) setSelectedRef(accounts[0].ref);
  }, [accounts, selectedRef]);

  useEffect(() => {
    setSelectedRef('');
  }, [UserId]);

  const selectedAccount = accounts.find((account) => account.ref === selectedRef);
  const accountOptions = accounts.map((account) => ({
    value: account.ref,
    label: `${account.name} (${account.ref})`,
  }));

  // riskView only exists once an action has run; fall back to what the list reports.
  const isHeld = riskView?.held ?? selectedAccount?.held;

  useEffect(() => {
    setReason('');
  }, [holdTab]);

  const resetForm = () => {
    setReason('');
    setShowModal(false);
  };

  const refreshAccounts = () => queryClient.invalidateQueries({ queryKey: ['customer-managed-accounts', UserId] });

  const openConfirmation = () => {
    if (!selectedRef) {
      Alert.error(
        accountsQuery.isLoading ? 'Still loading this customer’s managed accounts.' : 'No managed account to act on.',
      );
      return;
    }
    if (!reason.trim()) {
      Alert.error(
        holdTab === 'hold'
          ? 'Kindly enter why the account is being held.'
          : 'Kindly enter why the hold is being cleared.',
      );
      return;
    }
    setShowModal(true);
  };

  return (
    <div className="hold-manager">
      <div className="hold-manager__tabs">
        <div
          className={holdTab === 'hold' ? 'active' : ''}
          onClick={() => {
            !isRunningHoldAction && setHoldTab('hold');
          }}
        >
          Hold Account
        </div>
        <div
          className={holdTab === 'unhold' ? 'active' : ''}
          onClick={() => !isRunningHoldAction && setHoldTab('unhold')}
        >
          Unhold Account
        </div>
      </div>

      <div className="hold-manager__content">
        {isHeld !== undefined && (
          <p className={`hold-manager__content__status ${isHeld ? 'held' : ''}`}>
            {isHeld ? 'This account is currently on hold.' : 'This account has no open hold.'}
          </p>
        )}

        {!accountsQuery.isLoading && !accounts.length && (
          <p className="hold-manager__content__status">{`This customer has no managed account to ${holdTab}.`}</p>
        )}

        {accounts.length >= 1 && (
          <Select
            label="Managed Account"
            {...style}
            labelStyle={{ color: '#666766' }}
            backgroundColor="#F9FAFB"
            outlineColor="none"
            height="60px"
            placeholder="Select the account"
            value={selectedRef}
            options={accountOptions}
            onChange={(value: any) => setSelectedRef(value ?? '')}
          />
        )}

        <div className="reason-field">
          <p>{holdTab === 'hold' ? 'Hold Reason' : 'Clear Note'}</p>
          <textarea
            name="hold-reason"
            placeholder={holdTab === 'hold' ? 'Enter hold reason' : 'Enter reason for clearing the hold'}
            rows={4}
            cols={50}
            maxLength={255}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        <Select
          label={holdTab === 'hold' ? 'Held By' : 'Unheld By'}
          {...style}
          labelStyle={{ color: '#666766' }}
          backgroundColor="#F9FAFB"
          outlineColor="none"
          height="60px"
          placeholder=""
          value={userName}
          key={reason}
          style={{
            pointerEvents: 'none',
          }}
          options={[
            {
              value: userName,
              label: userName,
            },
          ]}
        />

        <div className="hold-manager__content__actions">
          <Button
            type="submit"
            {...OverideButtonStyle}
            disabled={isRunningHoldAction}
            style={{
              fontFamily: 'Jakarta',
              color: '#212337',
              flex: 1,
              margin: '10px 0 20px',
            }}
            color="#F7F7F7"
            onClick={resetForm}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            {...OverideButtonStyle}
            loading={isRunningHoldAction}
            style={{
              fontFamily: 'Jakarta',
              flex: 1,
            }}
            onClick={openConfirmation}
          >
            {holdTab === 'hold' ? 'Hold Account' : 'Unhold Account'}
          </Button>
        </div>
      </div>

      <ModalExecuteAction
        modalIsOpen={showModal}
        setIsOpen={(state: boolean) => {
          setShowModal(state);
          if (!state) {
            dispatch(setModalActions({ ...modalActions, password: '' }));
          }
        }}
        action={() => {
          if (holdTab === 'hold') {
            placeHold(selectedRef, reason.trim(), () => {
              resetForm();
              refreshAccounts();
            });
          } else {
            clearHold(selectedRef, reason.trim(), () => {
              resetForm();
              refreshAccounts();
            });
          }
        }}
        text={holdTab === 'hold' ? 'place a hold on this account?' : 'clear the hold on this account?'}
        actionColor={'#1734ca'}
        isLoading={isRunningHoldAction}
      />
    </div>
  );
}
